/**
 * 상태 배지 — §14 "LIVE·확인필요·따라잡는중".
 * @cupia/ui 의 Badge variant 를 그대로 쓰고, 의미론만 이 앱이 정한다.
 */

import { Badge } from '@cupia/ui/badge'
import type { RoomView } from '../lib/reducer'
import type { CaptionFinalData } from '../lib/events'

/** LIVE / 일시중단 / 종료 — 방 상태(§9) */
export function LiveBadge({ room }: { room: RoomView }): React.ReactElement {
  switch (room.state) {
    case 'live':
      return (
        <Badge variant="destructive" className="gap-1.5">
          <span
            className="inline-block h-1.5 w-1.5 rounded-full bg-current"
            aria-hidden
          />
          LIVE
        </Badge>
      )
    case 'degraded':
      return <Badge variant="softWarn">지연</Badge>
    case 'model_down':
      return <Badge variant="softDanger">모델 중단</Badge>
    case 'suspended':
      return <Badge variant="softGray">일시중단</Badge>
    case 'closed':
      return <Badge variant="softGray">종료됨</Badge>
    default:
      return <Badge variant="softGray">대기</Badge>
  }
}

/**
 * 따라잡는 중 — §7.3 L2(병합) 이상에서 표시.
 * L1 은 배너(alert)로만 알리고 배지는 L2 부터 띄운다(설계표의 "L2 … UI
 * '따라잡는 중' 배지").
 */
export function LagBadge({ room }: { room: RoomView }): React.ReactElement | null {
  if (room.level < 2) return null
  return (
    <Badge variant="softWarn" title={`적체 ${(room.lagMs / 1000).toFixed(1)}s (L${room.level})`}>
      따라잡는 중
    </Badge>
  )
}

/**
 * 확인 필요 — §6.5.
 *
 * **판정의 정본은 서버다.** 서버 후검증(§6.5)은 스크립트 비율·숫자 보존·상투구
 * 차단·2열 붕괴처럼 **클라이언트가 알 수 없는 근거**로 깃발을 세운다. 화면이
 * conf/flag/cut 만 보고 다시 판정하면 그 깃발들이 조용히 사라진다 — "확신
 * 없으면 표시"(§1 원칙 4)가 무력화된다.
 *
 * 그래서 `needs_review` 가 오면 그것을 따르고, 없을 때만(구 서버·목) 로컬
 * 문턱으로 파생한다.
 */
export const CONF_MIN = 0.55

export function needsReview(data: CaptionFinalData): boolean {
  if (typeof data.needs_review === 'boolean') return data.needs_review
  return data.conf < CONF_MIN || data.flag !== '' || data.cut
}

/** 배지 툴팁 사유 — 서버가 준 사유가 있으면 그것이 가장 정확하다. */
function reviewReason(data: CaptionFinalData): string {
  const codes = (data.review_reason ?? '').split(',').filter(Boolean)
  if (codes.length > 0) {
    return codes.map((c) => REASON_LABELS[c] ?? c).join(' · ')
  }
  if (data.flag !== '') return data.flag
  if (data.cut) return '발화 분할'
  return `신뢰도 ${(data.conf * 100).toFixed(0)}%`
}

/** 서버 review_reason 코드 → 사람 문구(§6.5 게이트 이름). */
const REASON_LABELS: Record<string, string> = {
  low_conf: '낮은 신뢰도',
  model_flag: '모델이 재확인을 요청함',
  cut: '발화 분할',
  script_ratio: '번역문에 타깃 언어가 아닌 문자가 많음',
  numbers: '숫자가 원문과 다름',
  boilerplate_suspect: '상투구(환각) 의심',
  text_not_source: '원문 자리에 번역이 들어옴',
  repetition_src: '원문 전사가 반복으로 뭉개짐',
  gate_suspect: '게이트가 환각을 의심했습니다',
  mixed_script: '원문에 두 문자체계가 섞여 있습니다',
  repetition: '원문 반복 붕괴',
  empty_translation: '번역이 비어 있음',
  passthrough_diverge: '2차 전사와 크게 다릅니다 — 원문 확인 필요',
  // DEF-022 G2(GATE_DESIGN §5.5). 라벨은 **항상** 존재하지만 배지 자체는
  // TRANS_LANGID_XCHECK_BADGE=0 기본값이라 P1~P3-2 에서는 화면에 뜨지
  // 않는다(배선은 살아 있고 기능만 휴면). 라벨을 미리 두는 이유는
  // 인벤토리 계약 테스트가 서버 REVIEW_* 상수와 이 표를 맞추기 때문이다 —
  // 없으면 화면이 원시 코드를 툴팁에 띄운다.
  lang_disagree: '언어 판정이 2차 전사와 다릅니다 — 원문 확인 필요',
  // 다중 귀 중재(DEF-026, MULTIEAR_DESIGN §8.5). 위와 같은 이유로 라벨을
  // 미리 둔다 — 기본은 꺼져 있고(TRANS_MULTIEAR_BADGE=0) 배선만 산다.
  multiear_diverge: '여러 인식기의 결과가 어긋납니다 — 원문 확인 필요',
  // G4 판정기 배지(DEF014_INTERFACE_FREEZE §3 결정표 `assistant_judge`).
  // 위와 같은 이유로 라벨을 미리 둔다 — 기본은 꺼져 있고
  // (TRANS_PERSONA_JUDGE_BADGE=0) 배선만 산다.
  assistant_judge: '통역기가 지어낸 답변일 수 있습니다 — 원문 확인 필요',
  // D7 게이트 구제 동률 처리(DEF-040 §8 결정 3) — 두 후보 모두 유효할 때
  // 3단계 동률 규칙으로 하나를 골랐다는 표시.
  rescue_tiebreak: '두 언어 후보 중 하나를 규칙으로 골랐습니다 — 원문 확인 필요',
}

export function ReviewBadge({ data }: { data: CaptionFinalData }): React.ReactElement | null {
  if (!needsReview(data)) return null
  return (
    <Badge variant="warning" title={reviewReason(data)}>
      확인 필요
    </Badge>
  )
}

/**
 * 추론됨 — §6.5-7.
 *
 * "확인 필요"와 **다른 것을 말하는 배지**다. 확인 필요는 행동 요구("사람이
 * 검토하라")고, 이쪽은 **내용의 성질**을 말한다: 이 문장은 회의에서 오간
 * 말이 아니라 모델이 지어낸 것으로 보인다.
 *
 * 사용자 보고(2026-08-07)가 요구한 것이 정확히 이 구분이었다 — "추론됨,
 * 예측됨, 정확치 않음 이런 게 표기되어야 하는데 다 정상으로 인지한 것처럼
 * 나오고 있어". 그래서 문구도 그 사용자가 말한 낱말을 쓴다.
 *
 * **서버 판정만 따른다.** 화면이 자체 문턱으로 파생하지 않는 이유는
 * `needsReview` 와 같다: 판별 근거(전사 원문·서버 실측 발화 길이)가
 * 클라이언트에 없다. 없으면 안 그리는 것이 지어내는 것보다 낫다.
 */
export function InferredBadge({
  data,
}: {
  data: CaptionFinalData
}): React.ReactElement | null {
  if (data.inferred !== true) return null
  return (
    <Badge variant="softDanger" title={inferredReason(data)}>
      추론됨
    </Badge>
  )
}

/** 서버 inferred_reason 코드 → 사람 문구(§6.5-7). */
const INFERRED_LABELS: Record<string, string> = {
  // 서버 축 이름과 **정확히** 일치해야 한다. 어긋나면 툴팁이 코드값(raw)로
  // 새거나 일반 문구로 떨어진다. assistant_persona 는 S15(2026-08-08)에서
  // 열등 검출기(assistant_register)를 우월판으로 되돌리며 바뀐 이름이다.
  assistant_persona: '회의 발언이 아니라 AI 응대문으로 보입니다',
  assistant_register: '회의 발언이 아니라 AI 응대문으로 보입니다', // 구 서버 하위호환
  off_language: '이 회의의 언어가 아닌 말이 들렸습니다',
  collapsed_phrase: '같은 구절이 되풀이돼 전사가 무너졌습니다',
  over_dense: '발화 길이에 담길 수 없는 분량입니다',
}

function inferredReason(data: CaptionFinalData): string {
  const codes = (data.inferred_reason ?? '').split(',').filter(Boolean)
  const parts = codes.map((c) => INFERRED_LABELS[c] ?? c)
  // 사유를 못 받았어도(구 서버) 배지의 뜻만은 말해 준다 — 빈 툴팁은
  // "왜 붙었는지 알 수 없는 배지"가 되어 신뢰를 깎는다.
  if (parts.length === 0) return '들린 내용이 아니라 추론된 문장으로 보입니다'
  return `${parts.join(' · ')} — 정확하지 않을 수 있습니다`
}

/**
 * 방향 표시 — `vi → ko`(§16 TRANS_SRC/TRANS_DST).
 *
 * **passthrough 카드는 방향이 없다**(§6.5): 타깃 언어 발화를 들린 그대로
 * 실은 것이라 통역이 아니다. "vi → ko" 를 붙이면 통역 결과로 오독된다 —
 * 그래서 그 경우 발화 언어 코드 하나만 보여 준다.
 *
 * **원어 자리는 증명된 값만 찍는다**(S38-2). `src`/`dst`(passthrough
 * 한정) 는 이제 `detected_lang` 을 받는다 — 라틴 문자권끼리(vi·en·fr…)는
 * 이 시스템이 구별하지 못해 빈 문자열로 온다. 빈 값을 "ko"·"vi" 처럼
 * 단정적으로 그리면 실제 회의에서 영어가 fr 로 찍히는 사고가 재현된다 —
 * 그래서 값이 없으면 언어 코드를 아예 찍지 않는다("이 발화의 언어" 라는
 * 단언도 값이 없을 때는 쓰지 않는다).
 */
export function DirectionBadge({
  src,
  dst,
  passthrough = false,
}: {
  src: string
  dst: string
  passthrough?: boolean
}): React.ReactElement {
  if (passthrough) {
    if (!dst) {
      return (
        <Badge variant="code" title="통역 대상이 아닌 발화입니다">
          원문
        </Badge>
      )
    }
    return (
      <Badge variant="code" title="이 발화의 언어 — 통역이 아니라 들린 그대로입니다">
        {dst}
      </Badge>
    )
  }
  if (!src) {
    return (
      <Badge variant="code" title="통역 방향">
        → {dst}
      </Badge>
    )
  }
  return (
    <Badge variant="code" title="통역 방향">
      {src} → {dst}
    </Badge>
  )
}

/**
 * 화자 **전환 표시** — "여기서 말하는 사람이 바뀐 것 같다" 만 말한다.
 *
 * 정체성(칩·화자별 색·이름 지정)은 폐기했다(사용자 결정 2026-08-05):
 * "누구인지는 안 알려줘도 되고 화자가 변경됨 감지 정도면 된다".
 * 그 결정이 화면을 더 정직하게 만든다 — 틀렸을 때의 피해가 "없던 구분선"
 * 이지 **남의 말에 붙은 잘못된 이름**이 아니다.
 *
 * 표시 규율(조용하게):
 * - `changed !== true` 면 **아무것도 그리지 않는다**. 판정을 못 했거나(짧은
 *   발화·무음·첫 발화) 안 바뀐 것이고, 둘 다 화면에 할 말이 없다.
 * - 읽기 보조지 주장이 아니다 — 배지가 아니라 **가느다란 구분선**이다.
 *   회의실 프로젝터에서 시선을 뺏지 않아야 한다.
 */
export function SpeakerTurnMark({
  changed,
}: {
  changed?: boolean | undefined
}): React.ReactElement | null {
  if (changed !== true) return null
  return (
    <span
      data-testid="speaker-turn"
      className="my-1 flex items-center gap-2 text-(length:--text-xs-size) text-[var(--color-muted-foreground)]"
    >
      <span className="h-px flex-1 bg-[var(--color-border)]" aria-hidden />
      화자가 바뀐 것으로 보입니다
      <span className="h-px flex-1 bg-[var(--color-border)]" aria-hidden />
    </span>
  )
}

/**
 * §14 듣는중 / 통역 중 — **서버 VAD 가 알려 주는** 발화 상태.
 *
 * 로컬 마이크 레벨로 추측하지 않는다: 서버의 실제 분절 판정과 어긋나면
 * "듣는중" 인데 자막이 안 나오거나 그 반대가 되어 신뢰를 잃는다.
 */
export function ListeningBadge({
  live,
}: {
  live: { state: 'open' | 'inflight'; src: string } | null
}): React.ReactElement {
  if (!live) {
    return (
      <Badge variant="softGray" title="발화를 기다리는 중입니다">
        대기
      </Badge>
    )
  }
  if (live.state === 'inflight') {
    return (
      <Badge variant="softInfo" className="gap-1.5" title="모델이 통역하는 중입니다">
        <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-current" aria-hidden />
        통역 중…
      </Badge>
    )
  }
  return (
    <Badge
      variant="softSuccess"
      className="gap-1.5"
      title="발화를 잡고 있습니다(서버 VAD 기준)"
    >
      <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-current" aria-hidden />
      {live.src.toUpperCase()} 듣는중
    </Badge>
  )
}

/** 강제 절단 — §5 20s 절단으로 분할된 발화 */
export function CutBadge({ data }: { data: CaptionFinalData }): React.ReactElement | null {
  if (!data.cut) return null
  return (
    <Badge variant="softGray" title="20초 강제 절단으로 분할된 발화">
      발화 분할
    </Badge>
  )
}
